import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import FormField, { ErrorSummary } from '../common/FormField';
import { Plus, Edit2, Trash2, Upload } from 'lucide-react';
import ImportModal from '../common/ImportModal';
import { formatDate } from '../../utils/helpers';
import { validators, validateForm } from '../../utils/validation';

// Transaction types — values MUST match the keys used by docNo() in AppContext.
export const TXN_TYPES = [
  { value: 'salesInvoice', label: 'Sales Invoice' },
  { value: 'salesReturn', label: 'Sales Return' },
  { value: 'deliveryChallan', label: 'Delivery Challan' },
  { value: 'salesOrder', label: 'Sales Order' },
  { value: 'purchaseBill', label: 'Purchase Bill' },
  { value: 'purchaseReturn', label: 'Purchase Return' },
  { value: 'purchaseOrder', label: 'Purchase Order' },
  { value: 'receipt', label: 'Party Receipt Entry' },
  { value: 'payment', label: 'Party Payment Entry' },
  { value: 'directReceipt', label: 'Other Receipt Entry' },
  { value: 'directPayment', label: 'Other Payment Entry' },
  { value: 'journal', label: 'Journal Voucher' },
  { value: 'expense', label: 'Expense Entry' },
  { value: 'contra', label: 'Contra Entry' },
  { value: 'stockJournal', label: 'Stock Journal' },
  { value: 'production', label: 'Production' },
  { value: 'stockIssue', label: 'Stock Issue' },
];
const TXN_LABEL = Object.fromEntries(TXN_TYPES.map(t => [t.value, t.label]));

const emptyPS = { txnType: 'salesInvoice', prefix: '', yearCode: '', startNo: 1, effectiveFrom: '' };

const buildPreview = (p) =>
  [p.prefix, p.yearCode, String(Number(p.startNo) || 1).padStart(4, '0')]
    .filter(v => v !== '' && v != null).join('-');

export default function PrefixSettingMaster() {
  const { state, dispatch } = useApp();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState(emptyPS);
  const [editing, setEditing] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const errors = useMemo(() => {
    const { errors } = validateForm({
      prefix: [validators.required(form.prefix, 'Prefix code')],
      startNo: [
        validators.required(form.startNo, 'Start no'),
        validators.positiveNonZero(form.startNo, 'Start no'),
      ],
    });
    return errors;
  }, [form]);

  const hasErrors = Object.values(errors).some(e => e);
  const touch = (f) => setTouched(p => ({ ...p, [f]: true }));

  const handleSave = () => {
    setSubmitted(true);
    setTouched({ prefix: true, startNo: true });
    if (hasErrors) return;
    const payload = { ...form, startNo: Number(form.startNo) || 1, prefix: (form.prefix || '').toUpperCase() };
    if (editing) {
      dispatch({ type: 'UPDATE_PREFIX_SETTING', payload: { ...payload, id: editing } });
      addToast('Prefix setting updated', 'success');
    } else {
      dispatch({ type: 'ADD_PREFIX_SETTING', payload });
      addToast('Prefix setting added', 'success');
    }
    close();
  };

  const close = () => { setShowForm(false); setForm(emptyPS); setEditing(null); setTouched({}); setSubmitted(false); };
  const openNew = () => { setForm(emptyPS); setEditing(null); setTouched({}); setSubmitted(false); setShowForm(true); };
  const openEdit = (row) => {
    setForm({ txnType: row.txnType, prefix: row.prefix || '', yearCode: row.yearCode || '', startNo: row.startNo || 1, effectiveFrom: row.effectiveFrom || '' });
    setEditing(row.id); setTouched({}); setSubmitted(false); setShowForm(true);
  };
  const handleDelete = async (row) => {
    const ok = await confirm(`Delete prefix setting for ${TXN_LABEL[row.txnType] || row.txnType}?`, { title: 'Delete Prefix Setting', variant: 'danger', confirmLabel: 'Delete' });
    if (ok) { dispatch({ type: 'DELETE_PREFIX_SETTING', payload: row.id }); addToast('Prefix setting deleted', 'success'); }
  };

  const columns = [
    { key: 'txnType', label: 'Transaction Name', sortable: true, render: v => TXN_LABEL[v] || v, exportValue: r => TXN_LABEL[r.txnType] || r.txnType },
    { key: 'prefix', label: 'Prefix Code' },
    { key: 'yearCode', label: 'Year Code', render: v => v || '-' },
    { key: 'startNo', label: 'Start No', align: 'right' },
    { key: 'effectiveFrom', label: 'Effective From', render: v => v ? formatDate(v) : '-', exportValue: r => r.effectiveFrom ? formatDate(r.effectiveFrom) : '' },
    { key: '__preview', label: 'Sample No', render: (_, r) => <span className="font-mono text-blue-700">{buildPreview(r)}</span>, exportValue: r => buildPreview(r) },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Prefix Setting</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowImport(true)} className="btn btn-secondary flex items-center gap-2"><Upload size={16} /> Import</button>
          <button onClick={openNew} className="btn btn-primary flex items-center gap-2"><Plus size={16} /> Add Prefix</button>
        </div>
      </div>

      <ImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        title="Import Prefix Settings"
        columns={[
          { key: 'txnType', label: 'Transaction Name', required: true },
          { key: 'prefix', label: 'Prefix Code', required: true },
          { key: 'yearCode', label: 'Year Code' },
          { key: 'startNo', label: 'Start No', type: 'number' },
          { key: 'effectiveFrom', label: 'Effective From (YYYY-MM-DD)' },
        ]}
        sample={{ txnType: 'Sales Invoice', prefix: 'INV', yearCode: '2526', startNo: 1, effectiveFrom: '2025-04-01' }}
        onAdd={(p) => {
          const t = TXN_TYPES.find(x => x.label.toLowerCase() === String(p.txnType || '').toLowerCase());
          dispatch({ type: 'ADD_PREFIX_SETTING', payload: { ...emptyPS, ...p, txnType: t ? t.value : p.txnType, prefix: (p.prefix || '').toUpperCase(), startNo: Number(p.startNo) || 1 } });
        }}
      />

      <div className="card">
        <DataTable
          columns={columns}
          data={state.prefixSettings || []}
          searchFields={['prefix', 'yearCode']}
          exportFilename="prefix-settings"
          emptyState={{ title: 'No prefix settings yet', description: 'Configure document numbering (prefix, year code, start no) per transaction. Until set, the default PREFIX-0001 numbering is used.', actionLabel: 'Add Prefix', onAction: openNew }}
          actions={(row) => (
            <>
              <button onClick={() => openEdit(row)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Edit"><Edit2 size={15} /></button>
              <button onClick={() => handleDelete(row)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={15} /></button>
            </>
          )}
        />
      </div>

      <Modal isOpen={showForm} onClose={close} title={editing ? 'Edit Prefix Setting' : 'Add Prefix Setting'} size="lg">
        <ErrorSummary errors={errors} show={submitted && hasErrors} />
        <div className="space-y-4">
          <FormField label="Transaction Name" required>
            <select className="input" value={form.txnType} onChange={e => setForm({ ...form, txnType: e.target.value })}>
              {TXN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </FormField>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <FormField label="Prefix Code" required error={errors.prefix} touched={touched.prefix || submitted} hint="e.g. INV">
              <input className="input font-mono uppercase" value={form.prefix} onChange={e => setForm({ ...form, prefix: e.target.value.toUpperCase() })} onBlur={() => touch('prefix')} maxLength={12} placeholder="INV" />
            </FormField>
            <FormField label="Year Code" hint="e.g. 2526">
              <input className="input font-mono" value={form.yearCode} onChange={e => setForm({ ...form, yearCode: e.target.value })} maxLength={12} placeholder="2526" />
            </FormField>
            <FormField label="Start No" required error={errors.startNo} touched={touched.startNo || submitted}>
              <input className="input text-right" type="number" min="1" value={form.startNo} onChange={e => setForm({ ...form, startNo: e.target.value })} onBlur={() => touch('startNo')} />
            </FormField>
            <FormField label="Effective From" hint="Applies on/after this date">
              <input className="input" type="date" value={form.effectiveFrom} onChange={e => setForm({ ...form, effectiveFrom: e.target.value })} />
            </FormField>
          </div>

          <div className="p-3 bg-blue-50 rounded-lg text-sm">
            <span className="text-gray-500">Sample document number: </span>
            <span className="font-mono font-semibold text-blue-700">{buildPreview(form)}</span>
            <p className="text-xs text-gray-400 mt-1">Numbering restarts each financial year and continues from Start No.</p>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button onClick={close} className="btn btn-secondary">Cancel</button>
          <button data-keyboard-save onClick={handleSave} className="btn btn-primary" disabled={submitted && hasErrors}>{editing ? 'Update' : 'Save'}</button>
        </div>
      </Modal>
    </div>
  );
}
